import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const CGVModal = ({ isOpen, onClose, onAccept }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1db584]">
            Conditions Générales de Vente et d&apos;Utilisation
          </DialogTitle>
          <DialogDescription>
            Baobab Connect - Plateforme de Transport de Marchandises
          </DialogDescription>
        </DialogHeader>
        
        <div className="h-[60vh] overflow-y-auto pr-4">
          <div className="space-y-6 text-sm text-gray-700">
            
            {/* Version et date */}
            <div className="text-center py-2 bg-gray-50 rounded">
              <p className="font-semibold">Version 1.0 - 10 Juillet 2025</p>
            </div>

            {/* 1. Introduction */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">1. Introduction</h3>
              <p className="mb-3 leading-relaxed">
                Les présentes Conditions Générales de Transport (CGT) régissent la relation contractuelle entre Baobab Connect, 
                société de transport et logistique (plateforme digitale mettant en relation expéditeurs et transporteurs), 
                d&apos;une part, et le Client (expéditeur ou donneur d&apos;ordre commandant une prestation de transport), d&apos;autre part.
              </p>
              <p className="mb-3 leading-relaxed">
                Baobab Connect agit en tant que prestataire de transport routier de marchandises et assume, vis-à-vis du Client, 
                les obligations du transporteur conformément aux présentes conditions.
              </p>
              <p className="mb-3 leading-relaxed">
                Ces CGT s&apos;appliquent à toutes les prestations de transport de marchandises confiées à Baobab Connect, que ce soit 
                pour des transports intra (transport national/local), conteneurisés, en camion auto-chargeuse, ou des transports 
                inter-pays (transfrontaliers).
              </p>
              <p className="font-semibold mt-3">Objectif :</p>
              <p className="mb-3 leading-relaxed">
                L&apos;objectif de ces conditions générales est d&apos;établir un cadre contractuel clair et juridiquement solide, 
                assurant une compréhension commune des modalités de service, y compris ce qui est inclus ou exclu du service, 
                les responsabilités de chacun, les limites de garantie et assurance, les conditions de paiement, ainsi que les 
                dispositions légales générales.
              </p>
            </section>

            {/* 2. Dispositions Générales */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">2. Dispositions Générales Applicables à Tous les Transports</h3>
              
              <h4 className="font-semibold text-gray-900 mt-4 mb-2">2.1 Obligations du Client</h4>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Fournir toutes les informations exactes et complètes concernant la marchandise (nature, poids, volume, dimensions, quantité, valeur déclarée, conditionnement)</li>
                <li>Remettre tous les documents nécessaires (lettre de voiture, documents douaniers, autorisations spécifiques) en temps utile</li>
                <li>Assurer un emballage approprié de la marchandise, garantissant une protection suffisante pour le transport routier</li>
                <li>Organiser les opérations de chargement et de déchargement (sauf convention contraire)</li>
                <li>Informer préalablement de toute marchandise dangereuse, périssable, animale vivante ou soumise à réglementations spéciales</li>
                <li>Assurer la présence d&apos;un destinataire habilité au lieu de livraison</li>
                <li>Payer le tarif du transport et les frais annexes selon les modalités convenues</li>
              </ul>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">2.2 Obligations de Baobab Connect</h4>
              <p className="mb-3 leading-relaxed">
                Baobab Connect s&apos;engage à exécuter la prestation de transport conformément aux termes convenus, incluant la mise 
                à disposition d&apos;un véhicule adapté avec chauffeur qualifié, pour acheminer la marchandise en bon état.
              </p>
              <p className="mb-3 leading-relaxed">
                Le transporteur garantit être dûment enregistré et autorisé à exercer l&apos;activité de transport public de 
                marchandises, conformément aux réglementations en vigueur (licences, assurances responsabilité civile).
              </p>
              <p className="mb-3 leading-relaxed">
                Baobab Connect fournit un suivi de l&apos;acheminement en temps réel via sa plateforme numérique (géolocalisation GPS, 
                notifications d&apos;étapes clés) et assure une assistance client 24/7.
              </p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">2.3 Responsabilité et régime juridique</h4>
              <p className="mb-3 leading-relaxed">
                Baobab Connect assume la responsabilité pour la garde et la bonne conduite de la marchandise pendant le transport, 
                dans les limites prévues par la législation en vigueur. En cas de perte, vol ou avarie, le Transporteur Partenaire 
                est responsable, sauf cas d&apos;exonération (faute du Client, vice propre, force majeure, fait d&apos;un tiers).
              </p>
            </section>

            {/* 3. Modalités spécifiques */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">3. Modalités Spécifiques par Type de Transport</h3>
              
              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3.1 Transport Intra (national, régional)</h4>
              <p className="mb-2"><strong>Prestations incluses :</strong> Camion adapté avec chauffeur, carburant, péages standards, suivi GPS, assurance de base.</p>
              <p className="mb-2"><strong>Franchise d&apos;immobilisation :</strong> 2 heures gratuites au chargement et au déchargement.</p>
              <p className="mb-3"><strong>Pénalités :</strong> Au-delà de la franchise, environ 12 500 FCFA par heure supplémentaire (100 000 FCFA/jour).</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3.2 Transport Conteneurisé</h4>
              <p className="mb-2"><strong>Prestations incluses :</strong> Transport conteneur depuis/vers port, attente port/scanner (≤5h), assurance cargo.</p>
              <p className="mb-2"><strong>Prestations exclues :</strong> Frais portuaires THC, scanner douane, droits de douane, TVA, BSC, frais de détention conteneur.</p>
              <p className="mb-2"><strong>Franchises :</strong> ½ journée au port/scanner, 1 jour pour douane destination, déchargement jusqu&apos;à fin du jour d&apos;arrivée.</p>
              <p className="mb-3"><strong>Pénalités :</strong> 100 000 FCFA/jour au-delà des franchises.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3.3 Transport Auto-chargeuse</h4>
              <p className="mb-2"><strong>Prestations incluses :</strong> Camion équipé grue/bras + chauffeur-opérateur, carburant, péages, chargement/déchargement avec grue, assurance.</p>
              <p className="mb-2"><strong>Franchise :</strong> 3 heures gratuites pour chaque opération de grue (chargement et déchargement).</p>
              <p className="mb-3"><strong>Pénalités :</strong> Environ 25 000 FCFA par heure supplémentaire au-delà de la franchise.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3.4 Transports Inter-pays (transfrontaliers)</h4>
              <p className="mb-2">Le Client est responsable des formalités douanières d&apos;exportation/importation. Baobab Connect émet une lettre de voiture internationale (CMR).</p>
              <p className="mb-2"><strong>Franchise douane frontière :</strong> 1 jour inclus.</p>
              <p className="mb-3"><strong>Pénalités :</strong> 100 000 FCFA/jour au-delà de la franchise douanière.</p>
            </section>

            {/* 4. Assurance */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">4. Assurance des Marchandises Transportées</h3>
              <p className="mb-3 leading-relaxed">
                Baobab Connect inclut un programme d&apos;assurance cargo pour chaque transport. Cette assurance couvre les dommages 
                matériels et le vol pouvant survenir au cours du transport (vol, accident, incendie, renversement).
              </p>
              <p className="mb-3 leading-relaxed">
                La couverture s&apos;applique du point de prise en charge au point de dépose convenu. Si la valeur de la cargaison 
                excède le plafond assuré, le Client doit le signaler à la commande pour souscrire une assurance complémentaire.
              </p>
              <p className="mb-2"><strong>Exclusions :</strong> Vice propre, mauvais conditionnement, pertes indirectes, retard simple, force majeure, marchandises exclues par la police.</p>
              <p className="mb-3"><strong>Procédure de sinistre :</strong> Réserves écrites immédiates à la livraison, puis notification écrite à Baobab Connect dans les 5 jours ouvrables.</p>
            </section>

            {/* 5. Paiement */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">5. Modalités de Paiement</h3>
              <p className="mb-2"><strong>Acompte :</strong> 70% du montant total à la prise en charge de la marchandise.</p>
              <p className="mb-2"><strong>Solde :</strong> 30% après la livraison effective.</p>
              <p className="mb-3"><strong>Moyens de paiement :</strong> Francs CFA (FCFA), Mobile Money, virement bancaire, chèque, espèces.</p>
              <p className="mb-3">
                <strong>Retard de paiement :</strong> Pénalités au taux légal + 5 points, indemnité forfaitaire de 100 000 FCFA pour frais de recouvrement.
              </p>
            </section>

            {/* 6. Dispositions Contractuelles */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">6. Dispositions Contractuelles Générales</h3>
              
              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6.1 Force Majeure</h4>
              <p className="mb-3 leading-relaxed">
                Aucune partie ne sera tenue responsable des retards ou manquements résultant d&apos;un cas de force majeure 
                (catastrophes naturelles, conflits armés, actes terroristes, épidémies, fermetures de routes/frontières, grèves générales).
              </p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6.2 Résiliation</h4>
              <p className="mb-3 leading-relaxed">
                En cas de manquement grave aux obligations, l&apos;autre partie peut prononcer la résiliation après mise en demeure 
                écrite restée sans effet pendant 5 jours ouvrables.
              </p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6.3 Réclamations et Litiges</h4>
              <p className="mb-2">
                Toute réclamation relative à la perte ou avarie doit être signalée dès la livraison par réserves écrites, 
                puis confirmée par écrit sous 5 jours ouvrables.
              </p>
              <p className="mb-3">
                <strong>Délai de prescription :</strong> 1 an à compter de la date de livraison (régime OHADA/CMR).
              </p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6.4 Loi applicable et juridiction</h4>
              <p className="mb-3 leading-relaxed">
                Les présentes CGT sont soumises au droit ivoirien et aux Actes Uniformes OHADA. Pour les opérations internationales, 
                les dispositions impératives de la Convention CMR s&apos;appliquent. En cas de différend, compétence expresse est 
                attribuée au Tribunal de Commerce d&apos;Abidjan.
              </p>
            </section>

            {/* 7. Confidentialité */}
            <section>
              <h3 className="font-bold text-lg text-[#1db584] mb-3">7. Clause de Non-Concurrence et Confidentialité</h3>
              <p className="mb-3 leading-relaxed">
                Les transporteurs partenaires s&apos;engagent à ne pas démarcher ni contracter directement avec le client final 
                pendant au moins 12 mois suivant la réalisation du transport. Le transporteur et Baobab Connect respectent la 
                confidentialité de toutes les informations sur la marchandise, le client, les itinéraires et les tarifs.
              </p>
            </section>

            {/* Contact */}
            <section className="border-t pt-4 mt-6 bg-gray-50 p-4 rounded">
              <h3 className="font-bold text-lg text-gray-900 mb-2">Contact</h3>
              <p className="mb-1">
                <strong>BAOBAB CONNECT</strong>
              </p>
              <p className="mb-1">Siège social : Abidjan, Côte d&apos;Ivoire</p>
              <p className="mb-1">Email : contact@baobabco.net</p>
              <p className="mb-1">Assistance 24/7 disponible via la plateforme</p>
              <p className="text-xs text-gray-500 mt-4">
                En adhérant à ces Conditions Générales de Transport, le Client reconnaît en avoir pris connaissance et les accepter sans réserve.
              </p>
            </section>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Refuser
          </button>
          <button
            onClick={onAccept}
            className="px-6 py-2 bg-[#1db584] text-white rounded-lg hover:bg-[#16a075] transition-colors font-medium"
          >
            J&apos;accepte les CGV
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CGVModal;
